import { supabase, toError } from '@/lib/supabase'
import type { Component, ComponentInsert, ComponentUpdate } from '@/types/database'
import { profileService } from './profileService'
import { activityLogService } from './activityLogService'
import { productComponentService } from './productComponentService'
import { captureError } from '@/lib/sentry'
import { getValidCurrencyCode } from '@/lib/currency'

export type ComponentWithUsers = Component & {
  creator?: { full_name: string | null } | null
  updater?: { full_name: string | null } | null
}

const COMPONENT_SELECT = '*, creator:profiles!components_created_by_fkey(full_name), updater:profiles!components_updated_by_fkey(full_name)'

export const componentService = {
  /**
   * Get all components for the current organization
   */
  async getComponents(): Promise<ComponentWithUsers[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('components')
      .select(COMPONENT_SELECT)
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      // Fallback if FK not yet available
      if (error.message?.includes('updated_by')) {
        const { data: fallback } = await supabase
          .from('components')
          .select('*, creator:profiles!components_created_by_fkey(full_name)')
          .eq('organization_id', orgId)
          .order('created_at', { ascending: false })
        return fallback ?? []
      }
      console.error('Error fetching components:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get a single component by ID
   */
  async getComponent(id: string): Promise<ComponentWithUsers | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return null

    const { data, error } = await supabase
      .from('components')
      .select(COMPONENT_SELECT)
      .eq('id', id)
      .eq('organization_id', orgId)
      .single()

    if (error) {
      console.error('Error fetching component:', error)
      return null
    }

    return data
  },

  /**
   * Get components by supplier
   */
  async getComponentsBySupplier(supplierId: string): Promise<Component[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('components')
      .select('*')
      .eq('organization_id', orgId)
      .eq('supplier_id', supplierId)
      .order('name')

    if (error) {
      console.error('Error fetching components by supplier:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get components by category
   */
  async getComponentsByCategory(category: Component['category']): Promise<Component[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('components')
      .select('*')
      .eq('organization_id', orgId)
      .eq('category', category)
      .order('name')

    if (error) {
      console.error('Error fetching components by category:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Create a new component
   */
  async createComponent(component: Omit<ComponentInsert, 'organization_id'>): Promise<Component | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    const sanitizedComponent = { ...component } as Record<string, unknown>
    if ('currency' in sanitizedComponent) {
      sanitizedComponent.currency = getValidCurrencyCode(sanitizedComponent.currency as string)
    }

    const { data, error } = await supabase
      .from('components')
      .insert({
        ...sanitizedComponent,
        organization_id: orgId,
        created_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating component:', error)
      captureError(error, { operation: 'createComponent', component_name: component.name, part_number: component.part_number })

      // Provide user-friendly error messages for known error codes
      if (error.code === '23505') {
        const friendlyError = new Error(
          `A component with this part number already exists. Please use a different part number.`
        )
        ;(friendlyError as any).code = error.code
        throw friendlyError
      }
      throw toError(error)
    }

    if (data) {
      activityLogService.log({
        action: 'created',
        entityType: 'component',
        entityId: data.id,
        entityName: data.name,
        details: { part_number: data.part_number, category: data.category },
      })
    }

    return data
  },

  /**
   * Update a component
   */
  async updateComponent(id: string, updates: ComponentUpdate): Promise<Component | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    // Sanitize payload: convert empty strings to null for UUID/numeric fields,
    // and strip NaN values that PostgREST would reject with a 400.
    const sanitized = { ...updates } as Record<string, unknown>
    if (sanitized.supplier_id === '') sanitized.supplier_id = null
    if (typeof sanitized.unit_price === 'number' && isNaN(sanitized.unit_price)) sanitized.unit_price = null
    if (typeof sanitized.stock_quantity === 'number' && isNaN(sanitized.stock_quantity)) sanitized.stock_quantity = 0
    if (typeof sanitized.min_stock_level === 'number' && isNaN(sanitized.min_stock_level)) sanitized.min_stock_level = null
    if ('currency' in sanitized) sanitized.currency = getValidCurrencyCode(sanitized.currency as string)

    const { data, error } = await supabase
      .from('components')
      .update({ ...sanitized, updated_by: user?.id })
      .eq('id', id)
      .eq('organization_id', orgId)
      .select()
      .single()

    if (error) {
      console.error('Error updating component:', error)
      captureError(error, { operation: 'updateComponent', component_id: id, fields: Object.keys(updates) })

      if (error.code === '23505') {
        throw new Error('A component with this part number already exists. Please use a different part number.')
      }
      if (error.code === '23514' && error.message?.includes('currency')) {
        throw new Error('Invalid currency code. Please select a valid ISO currency (e.g. GBP, EUR, USD).')
      }
      throw toError(error)
    }

    if (data) {
      activityLogService.log({
        action: 'updated',
        entityType: 'component',
        entityId: data.id,
        entityName: data.name,
        details: { fields_changed: Object.keys(updates) },
      })

      // When supplier_id changes, re-sync suppliers for all products using this component
      if ('supplier_id' in updates) {
        const usages = await productComponentService.getProductsUsingComponent(id)
        for (const usage of usages) {
          // Get ALL components for this product and re-sync the full set
          const allProductComponents = await productComponentService.getProductComponents(usage.product_id)
          const allComponentIds = allProductComponents.map((pc) => pc.component_id)
          await productComponentService.syncProductSuppliersFromComponents(usage.product_id, allComponentIds)
        }
      }
    }

    return data
  },

  /**
   * Delete a component
   */
  async deleteComponent(id: string): Promise<boolean> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')
    const existing = await this.getComponent(id)

    const { error } = await supabase
      .from('components')
      .delete()
      .eq('id', id)
      .eq('organization_id', orgId)

    if (error) {
      console.error('Error deleting component:', error)
      throw toError(error)
    }

    activityLogService.log({
      action: 'deleted',
      entityType: 'component',
      entityId: id,
      entityName: existing?.name,
    })

    return true
  },

  /**
   * Get component stats for dashboard
   */
  async getComponentStats(): Promise<{
    total: number
    byCategory: Record<string, number>
    ceMarked: number
    ukca_marked: number
  }> {
    const components = await this.getComponents()

    const byCategory = components.reduce((acc, component) => {
      const category = component.category || 'other'
      acc[category] = (acc[category] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const ceMarked = components.filter(c => c.ce_marked).length
    const ukca_marked = components.filter(c => c.ukca_marked).length

    return {
      total: components.length,
      byCategory,
      ceMarked,
      ukca_marked,
    }
  },

  /**
   * Search components by part number, name, or manufacturer part number
   */
  async searchComponents(query: string): Promise<Component[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    // Sanitize query to prevent PostgREST filter injection via special characters
    const sanitized = query.replace(/[,%().*]/g, '')
    if (!sanitized) return []

    const { data, error } = await supabase
      .from('components')
      .select('*')
      .eq('organization_id', orgId)
      .or(`name.ilike.%${sanitized}%,part_number.ilike.%${sanitized}%,manufacturer_part_number.ilike.%${sanitized}%`)
      .order('name')

    if (error) {
      console.error('Error searching components:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Find a component by exact part number (case-insensitive, scoped to org)
   */
  async findByPartNumber(partNumber: string): Promise<Component | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId || !partNumber.trim()) return null

    const { data, error } = await supabase
      .from('components')
      .select('*')
      .eq('organization_id', orgId)
      .ilike('part_number', partNumber.trim())
      .limit(1)
      .maybeSingle()

    if (error) {
      console.error('Error finding component by part number:', error)
      return null
    }
    return data
  },

  /**
   * Find a component by exact manufacturer part number (case-insensitive, scoped to org)
   */
  async findByManufacturerPartNumber(mpn: string): Promise<Component | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId || !mpn.trim()) return null

    const { data, error } = await supabase
      .from('components')
      .select('*')
      .eq('organization_id', orgId)
      .ilike('manufacturer_part_number', mpn.trim())
      .limit(1)
      .maybeSingle()

    if (error) {
      console.error('Error finding component by MPN:', error)
      return null
    }
    return data
  },

  /**
   * Find a component by name (case-insensitive, scoped to org)
   */
  async findByName(name: string): Promise<Component | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId || !name.trim()) return null

    const { data, error } = await supabase
      .from('components')
      .select('*')
      .eq('organization_id', orgId)
      .ilike('name', name.trim())
      .limit(1)
      .maybeSingle()

    if (error) {
      console.error('Error finding component by name:', error)
      return null
    }
    return data
  },

  /**
   * Match-first, create-second: find existing component by key fields,
   * or create a new one if no match found.
   * Match order: exact part_number → exact MPN → exact name
   */
  async upsertCanonicalComponent(
    input: Omit<ComponentInsert, 'organization_id'>
  ): Promise<{ component: Component; isExisting: boolean }> {
    // Try part number match first
    if (input.part_number) {
      const byPn = await this.findByPartNumber(input.part_number)
      if (byPn) return { component: byPn, isExisting: true }
    }

    // Try manufacturer part number
    if (input.manufacturer_part_number) {
      const byMpn = await this.findByManufacturerPartNumber(input.manufacturer_part_number)
      if (byMpn) return { component: byMpn, isExisting: true }
    }

    // Try name match
    if (input.name) {
      const byName = await this.findByName(input.name)
      if (byName) return { component: byName, isExisting: true }
    }

    // No match — create new
    const created = await this.createComponent(input)
    if (!created) throw new Error('Failed to create component')
    return { component: created, isExisting: false }
  },

  /**
   * Get component usage summary (how many products use each component)
   */
  async getComponentUsageSummary(): Promise<Array<{
    component_id: string
    component_name: string
    part_number: string
    products_using_count: number
    total_quantity_across_products: number
  }>> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('component_usage_summary')
      .select('*')
      .eq('organization_id', orgId)

    if (error) {
      console.error('Error fetching component usage summary:', error)
      return []
    }

    return (data ?? []).map(row => ({
      component_id: row.component_id,
      component_name: row.component_name,
      part_number: row.part_number,
      products_using_count: Number(row.products_using_count) || 0,
      total_quantity_across_products: Number(row.total_quantity_across_products) || 0,
    }))
  },

  /**
   * Bulk-update the currency label on specific components.
   * Does NOT convert prices — only changes the currency code.
   * If componentIds is provided, only those components are updated.
   * Otherwise updates all components in the org.
   */
  async bulkSetCurrency(currency: string, componentIds?: string[]): Promise<number> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const validCurrency = getValidCurrencyCode(currency)

    const { data: { user } } = await supabase.auth.getUser()

    let query = supabase
      .from('components')
      .update({ currency: validCurrency, updated_by: user?.id })
      .eq('organization_id', orgId)

    if (componentIds !== undefined) {
      if (componentIds.length === 0) return 0
      query = query.in('id', componentIds)
    }

    const { data, error } = await query.select('id')

    if (error) {
      console.error('Error bulk-updating currency:', error)
      throw toError(error)
    }

    const count = data?.length ?? 0

    activityLogService.log({
      action: 'updated',
      entityType: 'component',
      entityId: orgId,
      entityName: componentIds ? `${count} components` : 'All components',
      details: { field: 'currency', new_value: currency, components_affected: count },
    })

    return count
  },
}
